import React, { useEffect, useRef, useState } from 'react';
import { useDeviceType } from '~/context/common/DeviceContext';
import BrandImage from './BrandImage';
import DriveYouHappy from './DriveYouHappy';

const defaultInsetRadius = 24;

type InsetDimensions = {
  radius: number;
  x: number;
  y: number;
};

function getInsetDimensions(): InsetDimensions {
  // Do not animate the image's clip path if the user prefers reduced motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return { radius: 0, x: 0, y: 0 };
  }

  const maxWidth = Math.min(window.document.documentElement.clientWidth, 1800);
  const fallbackWidth = 1056;
  const fallbackMinInset = 44;

  const [, width, minInset] = new Array<[number, number, number]>(
    [375, 327, 24],
    [768, 582, 32],
    [1024, 796, 40],
    [Infinity, fallbackWidth, fallbackMinInset]
  ).find(([docWidth]) => maxWidth <= docWidth) ?? [
    0,
    fallbackWidth,
    fallbackMinInset
  ];

  const insetX = Math.max((maxWidth - width) / 2, minInset);

  return { radius: defaultInsetRadius, x: insetX, y: minInset };
}

export default function BrandMoment(props: React.PropsWithChildren) {
  const deviceType = useDeviceType();
  const parentRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const [imageInset, setImageInset] = useState<InsetDimensions>({
    radius: defaultInsetRadius,
    x: 0,
    y: 0
  });
  const [textOpacity, setTextOpacity] = useState(1);

  useEffect(() => {
    let rafId = 0;

    const updateScrollState = (eventType: string) => () => {
      const stickyEl = stickyRef.current;
      const parentEl = stickyEl?.parentElement;

      if (!stickyEl || !parentEl) return;

      const stickyRect = stickyEl.getBoundingClientRect();
      const parentRect = parentEl.getBoundingClientRect();

      const {
        radius: insetRadius,
        x: insetX,
        y: insetY
      } = getInsetDimensions();

      if (eventType === 'resize') {
        const wasScrolledPast = stickyRect.top < 0;

        setImageInset({
          radius: wasScrolledPast ? 0 : insetRadius,
          x: wasScrolledPast ? 0 : insetX,
          y: wasScrolledPast ? 0 : insetY
        });
      }

      if (stickyRect.top === 0) {
        const scrolledDistance = 0 - parentRect.top;
        const totalScrollableDistance = parentRect.height - stickyRect.height;
        const scrollPercentage = scrolledDistance / totalScrollableDistance;
        const clampedPercentage = Math.max(0, Math.min(1, scrollPercentage));
        const effectMultiplier = 2; // Speed up the effect

        setImageInset({
          radius: Math.max(
            insetRadius * (1 - clampedPercentage * effectMultiplier),
            0
          ),
          x: Math.max(insetX * (1 - clampedPercentage * effectMultiplier), 0),
          y: Math.max(insetY * (1 - clampedPercentage * effectMultiplier), 0)
        });
      }

      if (stickyRect.top < 0) {
        const scrolledDistance = 0 - stickyRect.top;
        const totalScrollableDistance = stickyRect.height / 2;
        const scrollPercentage = scrolledDistance / totalScrollableDistance;
        const clampedPercentage = Math.max(0, Math.min(1, scrollPercentage));
        const effectMultiplier = (() => {
          switch (true) {
            case deviceType.isMobile:
              return 3;
            case deviceType.isTablet:
              return 2.5;
            case deviceType.isDesktop:
            default:
              return 2;
          }
        })();

        setTextOpacity(1 - clampedPercentage * effectMultiplier);
      }

      rafId = 0;
    };

    updateScrollState('resize')();

    const handleScroll = (ev: Event) => {
      if (rafId) return;

      rafId = requestAnimationFrame(updateScrollState(ev.type));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    const observer = new ResizeObserver(_ => {
      if (rafId) return;

      rafId = requestAnimationFrame(updateScrollState('resize'));
    });

    if (parentRef.current) {
      observer.observe(parentRef.current);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();

      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [deviceType.isDesktop, deviceType.isMobile, deviceType.isTablet]);

  return (
    <div className="relative" ref={parentRef}>
      <div className="pointer-events-none sticky top-0 text-center">
        <DriveYouHappy variant="dark" style={{ opacity: textOpacity }} />
      </div>

      <div
        className="relative"
        style={
          {
            '--image-inset-radius': `${imageInset.radius}px`,
            '--image-inset-x': `${imageInset.x}px`,
            '--image-inset-y': `${imageInset.y}px`
          } as React.CSSProperties
        }
      >
        <div className="sticky top-0" ref={stickyRef}>
          <div
            style={{
              clipPath:
                'inset(var(--image-inset-y) var(--image-inset-x) round var(--image-inset-radius))'
            }}
          >
            <div className="pointer-events-none fixed top-0 mx-auto w-full max-w-[1800px] text-center">
              <DriveYouHappy variant="light" style={{ opacity: textOpacity }} />
            </div>

            <BrandImage className="flex justify-center" />
          </div>

          {props.children}
        </div>

        <div className="h-screen" />
      </div>
    </div>
  );
}
